import { useState } from "react";
import { toast } from "sonner";
import { api, get, post, errMsg, dt, label } from "@/lib/api";
import { PageHeader, Card, StatusBadge, Btn, Table, Loading, useFetch } from "@/components/kit";

export function ImportLeads() {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [summary, setSummary] = useState(null);
  const [skip, setSkip] = useState(true);
  const preview = async (mapping) => {
    const fd = new FormData();
    fd.append("file", file);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try { setPrev((await api.post("/leads/import/preview", fd)).data.data); setSummary(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (i, k, v) => setPrev({ ...prev, rows: prev.rows.map((r, j) => (j === i ? { ...r, data: { ...r.data, [k]: v } } : r)) });
  const commit = async () => { try { const r = await post("/leads/import/commit", { rows: prev.rows.map((r) => r.data), skip_duplicates: skip }); setSummary(r.data); toast.success(r.message); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <PageHeader title="CSV lead import" subtitle="Columns: Name, Mobile, Email, Product, Loan Amount, Source, Campaign, City, Income" />
      <Card title="1. Upload file">
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="csv-file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} />
          <Btn data-testid="csv-preview-btn" disabled={!file} onClick={() => preview(null)}>Preview & validate</Btn>
          <a className="text-xs font-semibold text-blue-800" href={`data:text/csv;charset=utf-8,${encodeURIComponent("Name,Mobile,Email,Product,Loan Amount,Source,Campaign,City,Income\nRahul Verma,9812345670,rahul@example.com,PL,300000,FACEBOOK,Diwali_PL,Ahmedabad,45000\n")}`} download="finorbit_leads_template.csv">Download template</a>
        </div>
      </Card>
      {prev && (
        <>
          <Card title="2. Column mapping">
            <div className="grid gap-3 md:grid-cols-4">
              {prev.headers.map((h) => (
                <label key={h} className="text-xs"><span className="font-semibold">{h}</span>
                  <select data-testid={`map-${h}`} className="fo-input mt-1" value={prev.mapping[h] || ""} onChange={(e) => setPrev({ ...prev, mapping: { ...prev.mapping, [h]: e.target.value || null } })}>
                    <option value="">Ignore</option>{prev.fields.map((f) => <option key={f} value={f}>{label(f)}</option>)}
                  </select></label>
              ))}
            </div>
            <Btn variant="outline" className="mt-3" onClick={() => preview(prev.mapping)}>Re-validate with mapping</Btn>
          </Card>
          <Card title={`3. Review - ${prev.summary.total} rows · ${prev.summary.valid} valid · ${prev.summary.errors} errors · ${prev.summary.duplicates} duplicates`} testid="import-preview">
            <div className="max-h-96 overflow-auto">
              <Table rows={prev.rows} cols={[{ k: "row", h: "#" },
                ...["full_name", "mobile", "email", "product", "loan_amount", "city"].map((k) => ({ k, h: label(k), r: (r) => <input className="w-28 rounded border px-1 py-0.5 text-xs" value={r.data[k] ?? ""} onChange={(e) => edit(r.row - 1, k, e.target.value)} /> })),
                { k: "e", h: "Issues", r: (r) => <span className={`text-xs ${r.errors.length ? "text-rose-600" : r.duplicate ? "text-amber-600" : "text-emerald-600"}`}>{r.errors.join(", ") || r.duplicate || "OK"}</span> }]} />
            </div>
            <div className="mt-3 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} />Skip duplicates</label>
              <Btn data-testid="csv-import-btn" variant="success" onClick={commit}>Import</Btn>
            </div>
          </Card>
        </>
      )}
      {summary && <Card title="Import summary" testid="import-summary"><p className="text-sm">Created <b>{summary.created}</b> · Skipped duplicates <b>{summary.skipped_duplicates}</b> · Failed <b>{summary.failed}</b></p>
        {summary.errors.map((e) => <p key={e.row} className="text-xs text-rose-600">Row {e.row}: {e.errors.join(", ")}</p>)}</Card>}
    </div>
  );
}

export function Duplicates() {
  const { data, reload } = useFetch(() => get("/leads/duplicates"), []);
  const act = async (l, action) => { try { await post(`/leads/${l.id}/duplicate-action`, { action }); toast.success(`${label(action)} done`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <PageHeader title="Duplicate leads" subtitle="Detected via configurable rules (mobile / PAN / email). Admin decides." />
      {data.length === 0 && <Card><p className="text-sm text-slate-400" data-testid="no-duplicates">No pending duplicates.</p></Card>}
      {data.map((l) => (
        <Card key={l.id} title={`${l.lead_code} · ${l.customer_name} · ${l.mobile} · ${label(l.source)}`} testid={`dup-${l.lead_code}`}
          action={<div className="flex gap-2"><Btn className="h-7 text-xs" onClick={() => act(l, "MERGE")}>Merge</Btn><Btn variant="outline" className="h-7 text-xs" onClick={() => act(l, "REOPEN")}>Reopen existing</Btn><Btn variant="outline" className="h-7 text-xs" data-testid={`dup-keep-${l.lead_code}`} onClick={() => act(l, "KEEP")}>Keep as new</Btn></div>}>
          <Table rows={l.existing} cols={[{ k: "lead_code", h: "Existing lead" }, { k: "app_code", h: "Application" }, { k: "source", h: "Source" }, { k: "assigned_employee_name", h: "Assigned" }, { k: "status", h: "Status", r: (x) => <StatusBadge value={x.status} /> }, { k: "created_at", h: "Created", r: (x) => dt(x.created_at) }]} />
        </Card>
      ))}
    </div>
  );
}
